import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Download, FilePlus2, Guitar, Pencil, Piano, Plus, Settings, X } from 'lucide-react';
import { executeCommand } from '@/commands/registry';
import { chordTonePitchClasses, type ChordTypeId } from '@/music/chords';
import { unrollChart } from '@/music/chart';
import { exportIRealURL, toIRealHTML } from '@/music/ireal';
import type { IRealChord } from '@/music/ireal/types';
import { parseKey, spellNoteInKey } from '@/music/notes';
import {
  generateChordVoicing,
  hasFlatNineAvoidInterval,
  shouldUseVoicing,
  topNoteOf,
  topVoiceToneName,
  voicingIdentityLabel,
  type Inversion,
} from '@/music/voicing';
import {
  familiesOf,
  isVoicingFamilySelection,
  voicingFamilyLabel,
  voicingFamilySelectionDescription,
  voicingFamilySelectionLabel,
  type VoicingFamilySelection,
} from '@/music/voicingFamilies';
import { AVAILABLE_VOICING_FAMILY_SELECTION_IDS } from '@/state/voicingAvailability';
import { allAlternativeShapes, type AlternativeShape } from '@/music/alternativeShapes';
import { scoreToChart } from '@/music/score/convert';
import { editionScore, editionSheetLabel, isScoreEdition } from '@/music/score/edition';
import { readingLinkUrl } from '@/components/readingLink';
import { scoreHasLyrics } from '@/music/score/toSong';
import { activeReharmLayer } from '@/music/score/reharm';
import { CHORD_SYMBOL_STYLES } from '@/music/notation/chordLabel';
import { PIANO_COMP_FAMILIES, type PianoCompFamily } from '@/music/pianoComping';
import { effectivePianoFamily, PIANO_LEFT_HAND_FAMILIES } from '@/music/pianoLeftHand';
import type { CompInstrument } from '@/music/style';
import {
  VISUAL_STYLE,
  useStore,
  type NewTuneInput,
  type PlaylistTuneRef,
  type PlayheadTint,
  type TuneMapDetail,
  type TuneMapLayout,
  type ViewSurface,
  type VoicingLens,
  tuneArrangementOf,
} from '@/state/store';
import { tuneMap } from '@/music/tuneMap';
import { pinCount, pinLane } from '@/music/voicingPins';
import type { Score } from '@/music/score/types';
import { ChordTypeSelect, NoteSelect } from '@/components/pickers';
import { ChartView } from '@/components/ChartView';
import { TuneMapView } from '@/components/TuneMapView';
import { TuneMapCard } from '@/components/TuneMapCard';
import { PrintSheet } from '@/components/PrintSheet';
import { usePrintMode } from '@/components/printMode';
import { PlaylistsMenu } from '@/components/PlaylistsMenu';
import { chartAnalysisMarks } from '@/components/chartAnalysis';
import { unrolledPlayingId } from '@/components/unrolledPlayhead';
import {
  applyTuneRecipe,
  hasRecipe,
  parseTuneRecipe,
  withoutRecipe,
} from '@/components/tuneRecipe';
import { PagedSheet } from '@/components/PagedSheet';
import { ChartHeader } from '@/components/chartChrome';
import { GuitarChartView } from '@/components/GuitarChartView';
import { ImportPanel } from '@/components/ImportPanel';
import { MeasureEditor } from '@/components/MeasureEditor';
import { ScaleSlider } from '@/components/ScaleSlider';
import {
  VOICE_LEADING_ENGINES,
  voiceLeadingEngine,
  type VoiceLeadingEngineId,
} from '@/music/voiceLeadingEngines';
import { scaleVars, workspaceScaleKind } from '@/state/displayScale';
import { Popover } from '@/components/Popover';
import { useInspector } from '@/components/inspectorContext';
import { BandTransport } from '@/components/BandTransport';
import { bandPlaybackStatus } from '@/components/bandPlaybackStatus';
import { TuneWorkspaceHeader } from '@/components/TuneWorkspaceHeader';
import { NotationPane } from '@/components/NotationPane';
import { ArrangementPane } from '@/components/ArrangementPane';
import {
  lensCyclesForm,
  useBandArrangement,
  type OptimizedSeqChord,
} from '@/audio/useBandArrangement';
import { loadOpenbookScore, openbookLibrary } from '@/data/openbookLibrary';
import { loadRealbookScore, realbookLibrary } from '@/data/realbookLibrary';
import { libraryTunes, loadLibraryScore } from '@/data/libraryTunes';
import { ABC_LINK_PARAM } from '@/data/abcLink';
import { BUNDLED_SCORES, bundledScoreById, loadBundledScore } from '@/data/bundledScores';
import {
  bundledScoreRef,
  chordChartRef,
  linkMatch,
  openbookRef,
  realbookRef,
} from '@/data/tuneLink';
import { loadBundledTuneSearchIndex } from '@/data/useTuneSearchSources';
import { CHAPTERS } from '@/data/chapters';
import { useDialogInteraction } from '@/components/useDialogInteraction';
import { usePassageSelection } from '@/components/passageSelection';
import { FocusControls, FocusToggle } from '@/components/FocusControls';
import { useOwnHeight } from '@/components/useOwnHeight';
import { BandScopeRow } from '@/components/BandScopeRow';
import { useBandEditor } from '@/components/useBandEditor';
import { APP_ROUTES } from '@/navigation';
import { recordFeature } from '@/analytics/usage';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { BarsPerLineRow } from '@/components/BarsPerLineControl';

const TRANSPOSE_STEPS = [-1, 1];
const NO_ALTERNATIVE_SHAPES: readonly AlternativeShape[] = [];
const NO_PREFERRED_ALTERNATIVES: readonly string[] = [];
const VOICING_ROTATIONS = [0, 1, 2, 3] as const;
const PLAYHEAD_TINTS: { id: PlayheadTint; label: string; swatch: string }[] = [
  { id: 'green', label: 'Green', swatch: '#2f6f6a' },
  { id: 'amber', label: 'Amber', swatch: '#ffb703' },
  { id: 'blue', label: 'Blue', swatch: '#3b82f6' },
  { id: 'pink', label: 'Pink', swatch: '#ec4899' },
];
/** The Chords menu's diagram switch: what the voicings sheet DRAWS (display),
 * deliberately apart from the mixer's comp instrument (what SOUNDS). */
const CHORD_DIAGRAM_OPTIONS: { id: 'guitar' | 'piano'; label: string; hint: string }[] = [
  { id: 'guitar', label: 'Guitar', hint: 'Draw guitar grips on the voicings sheet' },
  {
    id: 'piano',
    label: 'Piano',
    hint: 'Draw piano keyboards on the voicings sheet — the mixer still decides what sounds',
  },
];
/** The Backing row: what the comp PLAYS — the diagram choices plus the pianist's left hand
 * alone, the trio texture (the right hand takes the melody). */
const BACKING_OPTIONS: { id: CompInstrument; label: string }[] = [
  ...CHORD_DIAGRAM_OPTIONS.map(({ id, label }) => ({ id, label })),
  { id: 'piano-left', label: 'Piano, left hand' },
];
/** The workspace's two-axis view model: reading surface × voicing lens. */
const VIEW_SURFACES: { id: ViewSurface; label: string; description: string }[] = [
  { id: 'chart', label: 'Chart', description: 'Read the tune as a chord chart' },
  { id: 'score', label: 'Score', description: 'Read the tune as staff notation' },
  {
    id: 'map',
    label: 'Map',
    description: 'Read the tune as cadence shapes relative to the home key',
  },
];
/** What the Map mode does with the chords, from most to least help. */
const MAP_DETAILS: { id: TuneMapDetail; label: string; description: string }[] = [
  { id: 'shown', label: 'Shown', description: 'The chords under every cell' },
  { id: 'dimmed', label: 'Dimmed', description: 'The chords dimmed until you tap a line' },
  { id: 'hidden', label: 'Hidden', description: 'The chords hidden until you tap a line' },
];
/** The Map mode's presentation: the card (one line a cell) or the bar grid. */
const MAP_LAYOUTS: { id: TuneMapLayout; label: string; description: string }[] = [
  { id: 'card', label: 'Card', description: 'One line a cell, under the section letters' },
  { id: 'grid', label: 'Grid', description: 'Brackets over the bars of the chart' },
];
const VOICING_LENSES: {
  id: VoicingLens;
  label: string;
  shortLabel: string;
  description: string;
}[] = [
  { id: 'none', label: 'Off', shortLabel: 'Off', description: 'No generated voicings' },
  {
    id: 'comping',
    label: 'Comping',
    shortLabel: 'Comp',
    description: 'Voice-led backing voicings',
  },
  {
    id: 'melody',
    label: 'Chord melody',
    shortLabel: 'Melody',
    description: 'Melody-led arranged voicings',
  },
];

type WorkspaceSheetId = 'chord' | 'measure';

function useCompactWorkspace() {
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(max-width: 700px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return compact;
}

function WorkspaceSheet({
  title,
  onClose,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useDialogInteraction<HTMLElement>(true, onClose);

  return (
    <>
      <button
        type="button"
        className="workspace-sheet-backdrop"
        aria-label="Close editor"
        onClick={onClose}
      />
      <section
        ref={dialogRef}
        className="workspace-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="workspace-sheet-head">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="close-circle workspace-sheet-close"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="workspace-sheet-body">{children}</div>
      </section>
    </>
  );
}

function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function slug(title: string) {
  return (
    title
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'chart'
  );
}

/**
 * Tune URLs — the permanent way to reference a tune:
 * `/workbench?score=<stable-id>&tune=<title>[&by=<composer>]` is the canonical
 * native-score link; the title and composer remain a readable legacy fallback.
 * Resolution order is the user's saved Scores, bundled native Scores, OpenBook,
 * then the loaded iReal library. Moving a catalogue listing therefore does not
 * break a native score URL, while old title URLs keep working.
 */
function useTuneUrl(
  chartTitle: string,
  chartComposer: string | undefined,
  currentScoreId: string | undefined,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const scoreParam = searchParams.get('score');
  const tuneParam = searchParams.get('tune');
  const byParam = searchParams.get('by');
  // Which corpus a shared title came from. Without it a realbook tune's URL
  // round-trips to the OPENBOOK tune of the same name (All Blues exists in
  // both — one is a grand-staff piano part, the other a lead sheet), so a
  // refresh silently swapped versions.
  const sourceParam = searchParams.get('source');
  // Which BOOK to open it in: `read=bb|eb|c-bass` is how a guitarist hands a
  // horn player the same tune in their key (the Reading row's Copy link). The
  // C book is the parameter's absence, so every link the app ever produced
  // still means what it meant. Arrival SETS the reader's persisted edition —
  // a trumpet player is a B♭ player on every chart after this one too.
  const readParam = searchParams.get('read');
  // An ABC import link (`?abc=`, components/AbcLink.tsx) owns the address bar
  // until it has been read: reflecting the tune on screen meanwhile would
  // write the OLD tune's name beside it, and the link's reading and recipe
  // belong to the tune it opens. AbcLink applies them and removes the link.
  const abcLinkPending = searchParams.has(ABC_LINK_PARAM);
  const scoreEdition = useStore((s) => s.scoreEdition);
  useEffect(() => {
    if (abcLinkPending) return;
    if (isScoreEdition(readParam)) useStore.getState().setScoreEdition(readParam);
  }, [readParam, abcLinkPending]);
  // A RECIPE (tuneRecipe.ts): how to look at the tune — view, lens, reharm
  // levers, engine… Applied once the tune has landed (or at once, when the
  // link names no tune), then stripped from the address bar, so a reader who
  // moves a lever afterwards is not snapped back on refresh.
  const recipeKey = searchParams.toString();
  const applyRecipeAndStrip = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const recipe = parseTuneRecipe(params);
    if (!hasRecipe(recipe)) return;
    applyTuneRecipe(recipe);
    setSearchParams(withoutRecipe(params), { replace: true });
  }, [setSearchParams]);
  useEffect(() => {
    if (abcLinkPending) return;
    if (scoreParam || tuneParam) return; // the loader applies it after landing
    applyRecipeAndStrip();
    // `recipeKey` is the params' identity; the callback reads them live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeKey, scoreParam, tuneParam, abcLinkPending]);
  // While a URL load is in flight the reflect effect must hold off, or the
  // initial render would rewrite the URL to the OLD chart before the load lands.
  // The run carries the store's load counter from when it started: once
  // another tune has arrived (the Tunes browser and the set strip load in
  // place, leaving the params alone) the link has lost, the run stands down
  // when its fetch settles, and the address bar follows the tune now open.
  const pendingRef = useRef<{ loadSeq: number } | null>(null);
  const tuneLoadSeq = useStore((s) => s.tuneLoadSeq);

  useEffect(() => {
    if (abcLinkPending) return;
    if (!scoreParam && !tuneParam) return;
    const wantedScoreId = scoreParam?.trim();
    const currentScore = useStore.getState().score;
    if (wantedScoreId && currentScore.id === wantedScoreId) return;
    if (!tuneParam && !wantedScoreId) return;
    const wanted = tuneParam?.trim().toLowerCase() ?? '';
    const wantedComposer = byParam?.trim().toLowerCase();
    const currentChart = useStore.getState().chart;
    const wantedSource = sourceParam?.trim().toLowerCase();
    const currentMatches =
      !wantedScoreId &&
      wanted === currentChart.title.trim().toLowerCase() &&
      (!wantedComposer || wantedComposer === (currentChart.composer ?? '').trim().toLowerCase()) &&
      (wantedSource !== 'realbook' || useStore.getState().score.id?.startsWith('realbook-')) &&
      (wantedSource !== 'library' || useStore.getState().score.id?.startsWith('library-'));
    if (currentMatches) {
      applyRecipeAndStrip();
      return;
    }
    let live = true;
    const run = { loadSeq: useStore.getState().tuneLoadSeq };
    pendingRef.current = run;
    // Every land point asks this, never `live` alone: a tune the reader opened
    // while the fetch was pending (and anything they did to it) must not be
    // replaced by the link they have moved on from.
    const stillWanted = () => live && useStore.getState().tuneLoadSeq === run.loadSeq;
    /**
     * Land on a deep-linked tune.
     *
     * `noteCurrentTuneRef` is the point: this resolver loads scores directly
     * rather than through `tuneSources`, so without it a tune arrived at by
     * link carries no reference, and "Add to playlist" saves a private copy of
     * a corpus tune instead of pointing at it. Links are how a tune gets
     * shared, so that was the common path, not the edge case.
     *
     * It must come AFTER the load — loading writes the score, and any musical
     * write clears the ref. The ref shapes match `tuneSources` exactly, so a
     * tune opened by link and the same tune opened from the browser are the
     * same row to dedupe and to "already in this playlist".
     */
    const landOn = (score: Score, ref: PlaylistTuneRef) => {
      const store = useStore.getState();
      store.loadScore(score);
      // A tune opened by link is a tune you opened: it joins the recents, and
      // so the history Top picks reads (Oct 2026). Picks, the library's lists
      // and the About drawer all open tunes this way.
      store.recordRecentTune(ref);
      store.noteCurrentTuneRef(ref);
      window.scrollTo({ top: 0, behavior: 'auto' });
      applyRecipeAndStrip();
    };

    void (async () => {
      try {
        const st = useStore.getState();
        // An explicit source wins outright — the default ranking below exists
        // for links that predate the parameter.
        if (wantedSource === 'realbook' && wanted) {
          try {
            const hit = linkMatch(await realbookLibrary(), wanted, wantedComposer, (e) => e);
            if (hit) {
              const sc = await loadRealbookScore(hit);
              if (stillWanted()) landOn(sc, realbookRef(hit));
              return;
            }
          } catch {
            /* fall through to the default ranking */
          }
        }
        // A library tune lives in the account backend, not the bundle: only
        // a link that names the library looks there.
        if (wantedSource === 'library' && wanted) {
          try {
            const hit = linkMatch(await libraryTunes(), wanted, wantedComposer, (e) => e);
            if (hit) {
              const sc = await loadLibraryScore(hit);
              if (stillWanted()) {
                landOn(sc, {
                  kind: 'library',
                  id: hit.id,
                  title: hit.title,
                  composer: hit.composer,
                });
              }
              return;
            }
          } catch {
            /* fall through to the default ranking */
          }
        }
        if (wantedScoreId) {
          const savedById = st.savedCharts.find((candidate) => candidate.id === wantedScoreId);
          if (savedById) {
            if (stillWanted()) {
              landOn(savedById, {
                kind: 'saved',
                id: wantedScoreId,
                title: savedById.title,
                composer: savedById.composer,
              });
            }
            return;
          }
          const bundled = bundledScoreById(wantedScoreId);
          if (bundled) {
            const nativeScore = await loadBundledScore(bundled);
            if (stillWanted()) {
              landOn(nativeScore, {
                kind: 'score',
                id: bundled.record.id,
                revisionId: bundled.revision.id,
                title: bundled.summary.title,
                composer: bundled.summary.composer,
              });
            }
            return;
          }
          if (!wanted) return;
        }
        // The order and the match are tuneLink.ts's, which the home page's
        // cards ask what to add to a playlist: a card files what its link opens.
        const saved = linkMatch(st.savedCharts, wanted, wantedComposer, (score) => score);
        if (saved) {
          if (stillWanted()) {
            // A saved Score with no id cannot be referenced; leaving the ref
            // null makes Add-to snapshot it, which is the honest fallback.
            if (saved.id) {
              landOn(saved, {
                kind: 'saved',
                id: saved.id,
                title: saved.title,
                composer: saved.composer,
              });
            } else {
              st.loadScore(saved);
              window.scrollTo({ top: 0, behavior: 'auto' });
              applyRecipeAndStrip();
            }
          }
          return;
        }
        const native = linkMatch(BUNDLED_SCORES, wanted, wantedComposer, (entry) => entry.summary);
        if (native) {
          const nativeScore = await loadBundledScore(native);
          if (stillWanted()) landOn(nativeScore, bundledScoreRef(native));
          return;
        }
        // The openbook corpus carries written melodies — when a title exists
        // both there and in the chord-only iReal library, the melody version
        // is the richer resolution for a shared link.
        try {
          const ob = linkMatch(await openbookLibrary(), wanted, wantedComposer, (e) => e);
          if (ob) {
            const sc = await loadOpenbookScore(ob);
            if (stillWanted()) landOn(sc, openbookRef(ob));
            return;
          }
        } catch {
          /* fall through to the iReal library */
        }
        // Nor fetch a whole library for a link nobody is waiting on.
        if (!stillWanted()) return;
        if (!st.playlist) {
          st.rehydratePlaylist();
          if (!useStore.getState().playlist) await executeCommand('open_library', {});
        }
        const loaded = useStore.getState().playlist;
        if (!stillWanted() || !loaded) return;
        let index = loaded;
        let song = linkMatch(loaded.songs, wanted, wantedComposer, (s) => s);
        if (!song) {
          // A list you imported is loaded and the tune is not in it: the chord
          // charts that ship with the app may have it, and a link to one of
          // them (a Top pick, the library's lists, the Corpus leagues) must not
          // stop at your import. Your import stays loaded.
          try {
            index = await loadBundledTuneSearchIndex();
          } catch {
            return;
          }
          if (!stillWanted()) return;
          song = linkMatch(index.songs, wanted, wantedComposer, (s) => s);
        }
        if (song) {
          // Not `landOn`: a chunk is parsed rather than loaded as a Score, and
          // the ireal ref stores the chunk itself, not an id.
          const store = useStore.getState();
          store.loadTuneFromChunk(index.scheme, song.chunk);
          const ref = chordChartRef(index, song);
          store.recordRecentTune(ref);
          store.noteCurrentTuneRef(ref);
          window.scrollTo({ top: 0, behavior: 'auto' });
          applyRecipeAndStrip();
        }
      } finally {
        // A newer run (the params moved on) owns the flag now.
        if (pendingRef.current === run) pendingRef.current = null;
      }
    })();
    return () => {
      live = false;
    };
  }, [scoreParam, tuneParam, byParam, sourceParam, applyRecipeAndStrip, abcLinkPending]);

  useEffect(() => {
    if (abcLinkPending) return;
    // Read the live chart: a warm playlist can resolve synchronously inside the
    // mount commit, leaving this render closure one tune behind.
    const liveChart = useStore.getState().chart;
    const liveScoreId = useStore.getState().score.id;
    const title = liveChart.title.trim();
    // An untitled tune has no name to link by, so the address bar drops the
    // name rather than keep the old one: left on the old title, a reload
    // found no tune of that name loaded and re-opened the corpus original
    // over the edited work. The composer and source only qualify a title.
    const composer = title ? (liveChart.composer?.trim() ?? '') : '';
    const pending = pendingRef.current;
    if (pending && pending.loadSeq === useStore.getState().tuneLoadSeq) return;
    const stableScore =
      !!liveScoreId &&
      (useStore.getState().savedCharts.some((saved) => saved.id === liveScoreId) ||
        bundledScoreById(liveScoreId) !== undefined);
    const liveSource = !title
      ? ''
      : liveScoreId?.startsWith('realbook-')
        ? 'realbook'
        : liveScoreId?.startsWith('library-')
          ? 'library'
          : '';
    // The address bar follows the reading too, so the link you share IS the
    // sheet you are looking at.
    const liveRead = useStore.getState().scoreEdition;
    const alreadyReflected =
      (tuneParam ?? '') === title &&
      (byParam ?? '') === composer &&
      (sourceParam ?? '') === liveSource &&
      (readParam ?? '') === (liveRead === 'c' ? '' : liveRead) &&
      (stableScore ? scoreParam === liveScoreId : !scoreParam);
    if (alreadyReflected) return;
    const next = new URLSearchParams(searchParams);
    if (stableScore && liveScoreId) next.set('score', liveScoreId);
    else next.delete('score');
    if (title) next.set('tune', title);
    else next.delete('tune');
    if (composer) next.set('by', composer);
    else next.delete('by');
    if (liveSource) next.set('source', liveSource);
    else next.delete('source');
    if (liveRead !== 'c') next.set('read', liveRead);
    else next.delete('read');
    setSearchParams(next, { replace: true });
  }, [
    chartTitle,
    chartComposer,
    currentScoreId,
    scoreEdition,
    scoreParam,
    tuneParam,
    byParam,
    sourceParam,
    readParam,
    searchParams,
    setSearchParams,
    tuneLoadSeq,
    abcLinkPending,
  ]);
}

export function SequenceBuilderPage() {
  const chart = useStore((s) => s.chart);
  const iRealExport = useMemo(() => exportIRealURL(chart), [chart]);
  const currentScoreId = useStore((s) => s.score.id);
  useTuneUrl(chart.title, chart.composer, currentScoreId);
  // A tune opened from anywhere lands at the TOP of its sheet (user ruling
  // Sep 2026: "new tunes should open at the top of the score if you've
  // opened from the bottom"). The set strip, the Tunes browser and the
  // palette all load from wherever the reader has scrolled — the strip lives
  // in the transport at the foot of the page — and a new tune that opened on
  // its last bars read as the old one. The URL land points already scroll;
  // this is the one seam every load path shares: the store's load counter.
  // NOT the tune's title and composer, which a rename in Tune details changes
  // too — keyed on those, every keystroke jumped the sheet back to the top.
  const tuneLoadSeq = useStore((s) => s.tuneLoadSeq);
  const lastTuneLoadSeq = useRef(tuneLoadSeq);
  useEffect(() => {
    if (lastTuneLoadSeq.current === tuneLoadSeq) return;
    lastTuneLoadSeq.current = tuneLoadSeq;
    window.scrollTo({ top: 0, behavior: 'auto' });
    // The map's quiz key is this tune's; the next tune opens as written.
  }, [tuneLoadSeq]);
  const [workspaceParams, setWorkspaceParams] = useSearchParams();
  const compactWorkspace = useCompactWorkspace();
  const stringSet = useStore((s) => s.stringSet);
  const setStringSet = useStore((s) => s.setStringSet);
  const freeStringSet = useStore((s) => s.freeStringSet);
  const setFreeStringSet = useStore((s) => s.setFreeStringSet);
  const voicingFamily = useStore((s) => s.voicingFamily);
  const setVoicingFamily = useStore((s) => s.setVoicingFamily);
  const avoidB9 = useStore((s) => s.avoidB9);
  // The topbar's contextual slot for tune actions (YouTube-shell rework):
  // present only on workbench routes, found after mount since the header
  // commits in the same pass as this page.
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderSlot(document.getElementById('topbar-tune-actions'));
  }, []);
  const [editMode, setEditMode] = useState(false);
  // The reading edition (B♭, E♭, bass clef) is a VIEW of the concert score:
  // the chart cell reads the projection of `editionScore`, so its symbols
  // move by the same diatonic rule the notation uses. Editing shows concert
  // — the chord editor writes concert roots, and the sheet must show what is
  // being written — and the lens cells stay concert too: the grips they draw
  // are a guitarist's or a pianist's.
  const score = useStore((s) => s.score);
  const scoreEdition = useStore((s) => s.scoreEdition);
  const readingChart = useMemo(
    () =>
      editMode || scoreEdition === 'c' ? chart : scoreToChart(editionScore(score, scoreEdition)),
    [chart, editMode, score, scoreEdition],
  );
  const readingEditionLabel = editMode ? null : editionSheetLabel(scoreEdition);
  // Export ▾ › Copy link: this tune, in the current reading (`read=` rides the
  // URL), for the horn player's phone.
  const [copiedLink, setCopiedLink] = useState(false);
  const [mobileSheet, setMobileSheet] = useState<WorkspaceSheetId | null>(null);
  const startingInversion = useStore((s) => s.startingInversion);
  const setStartingInversion = useStore((s) => s.setStartingInversion);
  const viewSurface = useStore((s) => s.viewSurface);
  const showAnalysis = useStore((s) => s.showAnalysis);
  const setViewSurface = useStore((s) => s.setViewSurface);
  const voicingLens = useStore((s) => s.voicingLens);
  const setVoicingLens = useStore((s) => s.setVoicingLens);
  const unrollRepeats = useStore((s) => s.unrollRepeats);
  const setUnrollRepeats = useStore((s) => s.setUnrollRepeats);
  const cycleForm = useStore((s) => s.cycleForm);
  const setCycleForm = useStore((s) => s.setCycleForm);
  const sheetPages = useStore((s) => s.sheetPages);
  const setSheetPages = useStore((s) => s.setSheetPages);
  // Page view is a desktop reading — on the compact workspace it degrades to
  // the plain sheet rather than a stack of one-column "pages".
  const pagesActive = sheetPages && !compactWorkspace;
  const tuneMapDetail = useStore((s) => s.tuneMapDetail);
  const setTuneMapDetail = useStore((s) => s.setTuneMapDetail);
  const tuneMapLayout = useStore((s) => s.tuneMapLayout);
  const setTuneMapLayout = useStore((s) => s.setTuneMapLayout);
  // The card prints through a second copy in a print sheet (PrintSheet.tsx),
  // mounted only while printing; the grid prints through its PagedSheet.
  const printing = usePrintMode();
  const chartScale = useStore((s) => s.chartScale);
  const setChartScale = useStore((s) => s.setChartScale);
  const voiceLeadingEngineId = useStore((s) => s.voiceLeadingEngine);
  const setVoiceLeadingEngine = useStore((s) => s.setVoiceLeadingEngine);
  const scoreViewPart = useStore((s) => s.scoreViewPart);
  const setScoreViewPart = useStore((s) => s.setScoreViewPart);
  const activeEngine = voiceLeadingEngine(voiceLeadingEngineId);
  const scoreScale = useStore((s) => s.scoreScale);
  const chordNameScale = useStore((s) => s.arrangeChordNameScale);
  const setChordNameScale = useStore((s) => s.setArrangeChordNameScale);
  const fretNumberScale = useStore((s) => s.arrangeFretNumberScale);
  const diagramMarkers = useStore((s) => s.arrangeDiagramMarkers);
  const setDiagramMarkers = useStore((s) => s.setArrangeDiagramMarkers);
  const setFretNumberScale = useStore((s) => s.setArrangeFretNumberScale);
  const showLyrics = useStore((s) => s.showLyrics);
  const setShowLyrics = useStore((s) => s.setShowLyrics);
  const showBarNumbers = useStore((s) => s.showBarNumbers);
  const chordMelodyDensity = useStore((s) => s.chordMelodyDensity);
  const setChordMelodyDensity = useStore((s) => s.setChordMelodyDensity);
  const setShowBarNumbers = useStore((s) => s.setShowBarNumbers);
  const scoreModel = useStore((s) => s.score);
  const hasLyrics = useMemo(() => scoreHasLyrics(scoreModel), [scoreModel]);
  // The shared tune header identifies the source in both workspaces.
  const setScoreScale = useStore((s) => s.setScoreScale);
  const playheadTint = useStore((s) => s.playheadTint);
  const setPlayheadTint = useStore((s) => s.setPlayheadTint);
  const diagramInstrument = useStore((s) => s.diagramInstrument);
  // The lane the Chords menu's Pinned row counts: the lens's plan × the drawn
  // instrument (voicingPins.ts).
  const tunePins = useStore((s) => tuneArrangementOf(s)?.pins);
  const clearVoicingPins = useStore((s) => s.clearVoicingPins);
  const currentPinLane = pinLane(
    voicingLens === 'melody' ? 'melody' : 'comping',
    diagramInstrument === 'piano' ? 'piano' : 'guitar',
  );
  const pinnedOnLane = pinCount(tunePins, currentPinLane);
  const setDiagramInstrument = useStore((s) => s.setDiagramInstrument);
  const chordSymbolStyle = useStore((s) => s.chordSymbolStyle);
  const setChordSymbolStyle = useStore((s) => s.setChordSymbolStyle);
  const setChartRepeats = useStore((s) => s.setChartRepeats);

  const selectedChordId = useStore((s) => s.selectedChordId);
  const selectedMeasureId = useStore((s) => s.selectedMeasureId);
  const insertionMeasureId = useStore((s) => s.insertionMeasureId);
  const selectChord = useStore((s) => s.selectChord);
  const selectMeasure = useStore((s) => s.selectMeasure);
  const clearPassage = useStore((s) => s.clearPassage);
  // Bars the active reharm layer overrides wear the dashed mark on both sheets.
  const reharmIdSet = useMemo(
    () => new Set(Object.keys(activeReharmLayer(scoreModel)?.overrides ?? {})),
    [scoreModel],
  );
  const { passageIdSet, onSelectPassage } = usePassageSelection();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (!useStore.getState().passage) return;
      clearPassage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearPassage]);
  const addChord = useStore((s) => s.addChord);
  const updateChord = useStore((s) => s.updateChord);
  const removeChord = useStore((s) => s.removeChord);

  const setChartStyle = useStore((s) => s.setChartStyle);
  const setTimeSignature = useStore((s) => s.setTimeSignature);
  const transpose = useStore((s) => s.transpose);
  const transposeToKey = useStore((s) => s.transposeToKey);

  const clearChart = useStore((s) => s.clearChart);
  const newTune = useStore((s) => s.newTune);
  const playlist = useStore((s) => s.playlist);
  const rehydratePlaylist = useStore((s) => s.rehydratePlaylist);

  // The Playlists page sends people here to fill an empty set: the Tunes
  // browser is the only place with an Add-to control, and the header verbs are
  // contextual, so arriving at a workbench with the browser SHUT would land
  // them one unexplained click short of what they came for.
  const wantsTunes = (useLocation().state as { openTunes?: boolean } | null)?.openTunes === true;
  const [showImport, setShowImport] = useState(wantsTunes);
  const [form, setForm] = useState<{
    root: string;
    chordType: ChordTypeId;
    duration: number;
    lead: string;
  }>({
    root: 'C',
    chordType: 'maj7',
    duration: 4,
    lead: '',
  });

  const studyContext = useMemo(() => {
    const chapterId = workspaceParams.get('study');
    const presetId = workspaceParams.get('task');
    if (!chapterId || !presetId) return null;
    const chapter = CHAPTERS.find((entry) => entry.id === chapterId);
    const study = chapter?.studyCharts?.find((entry) => entry.presetId === presetId);
    return chapter && study ? { chapter, study } : null;
  }, [workspaceParams]);

  // Learning links may configure a family without making that query parameter
  // an enduring override. Consume it once, then leave the persisted Workbench
  // selector in control.
  useEffect(() => {
    const requested = workspaceParams.get('family');
    if (!requested) return;
    if (isVoicingFamilySelection(requested) && requested !== voicingFamily) {
      setVoicingFamily(requested);
    }
    const next = new URLSearchParams(workspaceParams);
    next.delete('family');
    setWorkspaceParams(next, { replace: true });
  }, [workspaceParams, setWorkspaceParams, setVoicingFamily, voicingFamily]);

  const clearStudyContext = () => {
    const next = new URLSearchParams(workspaceParams);
    next.delete('study');
    next.delete('task');
    setWorkspaceParams(next, { replace: true });
  };

  const selectedChord = useMemo(() => {
    for (const m of chart.measures) {
      const c = m.chords.find((x) => x.id === selectedChordId);
      if (c) return c;
    }
    return null;
  }, [chart, selectedChordId]);

  // Restore a previously-loaded playlist (its raw source lives under a dedicated
  // localStorage key, separate from the persisted store) so it stays browsable
  // across reloads without re-pasting.
  useEffect(() => {
    if (!playlist) rehydratePlaylist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedAlternativeShapes = useStore((state) => state.alternativeShapes);
  const savedPreferredAlternativeIds = useStore((state) => state.preferredAlternativeIds);
  const alternativeShapes = useMemo(
    () => (studyContext ? NO_ALTERNATIVE_SHAPES : allAlternativeShapes(savedAlternativeShapes)),
    [studyContext, savedAlternativeShapes],
  );
  const preferredAlternativeIds = studyContext
    ? NO_PREFERRED_ALTERNATIVES
    : savedPreferredAlternativeIds;

  // The whole band — performance-order flattening, voice-led events, bass /
  // comp / drums / melody tracks, live option push and playAll — lives in the
  // shared useBandArrangement hook (the Score page mounts the same machinery).
  // The page keeps only its view concerns: the diagram grids, the unrolled
  // sheet, the cycle notes and the playhead follow.
  const band = useBandArrangement({
    arrangePlayback: voicingLens === 'melody',
    formCycleEnabled: lensCyclesForm(voicingLens),
    alternativeShapes,
    preferredAlternativeIds,
  });
  // The Chords menu's Backing and piano Family rows edit the band the scope
  // row names, like the mixer (components/bandScope.ts).
  const bandEditor = useBandEditor(band.bandSplit);
  const { compInstrument, compPianoFamily } = bandEditor.band;
  const patchBand = bandEditor.edit;
  // No chip is lit while the sections the scope reaches comp on different instruments.
  const backingIs = (id: string) =>
    !bandEditor.varies.has('compInstrument') && compInstrument === id;
  const {
    singleFlat,
    optimized,
    pianoOptimized,
    cycle,
    cycleActive,
    playingIndex,
    playingMeasureId,
  } = band;
  // What the voicings sheet DRAWS: the piano realization when the Chords menu
  // picks keyboards, else the grips. A READING choice — the mixer's comp
  // instrument decides what SOUNDS, and the two are deliberately independent.
  // PianoVoicedChord<OptimizedSeqChord> is a superset of OptimizedSeqChord, so
  // the measure maps and the grid types are untouched — DiagramCell just finds
  // `midis`/`hands`/`held` on the chords when piano diagrams are on.
  const sheetChords = pianoOptimized ?? optimized;
  const pianoSheet = !!pianoOptimized;

  // Something asked for playback — the set strip advancing, or a Play pressed
  // on a playlist before this page existed. The transport lives here, on the
  // page's ONE arrangement instance, so here is where the asking is answered.
  const playAll = band.playAll;
  const pendingPlay = useStore((s) => s.pendingPlay);
  const clearPendingPlay = useStore((s) => s.clearPendingPlay);
  useEffect(() => {
    if (!pendingPlay) return;
    clearPendingPlay();
    playAll();
  }, [pendingPlay, clearPendingPlay, playAll]);
  const openingHarmony = useMemo(
    () =>
      singleFlat
        .flatMap((measure) => measure.chords)
        .find((chord) => !chord.noChord && chord.chordType !== null),
    [singleFlat],
  );
  const openingTopVoices = useMemo(() => {
    if (!openingHarmony?.chordType) return [];
    const chordType = openingHarmony.chordType;
    const key = parseKey(chart.key?.replace(/-$/, ' minor')) ?? parseKey(openingHarmony.root);
    const tones = chordTonePitchClasses(openingHarmony.root, chordType).map((pitchClass) =>
      spellNoteInKey(pitchClass, key),
    );
    return VOICING_ROTATIONS.map((rotation) => ({
      rotation,
      note: tones[(rotation + tones.length - 1) % tones.length],
      tone: topVoiceToneName(chordType, rotation),
    }));
  }, [chart.key, openingHarmony]);

  // Follow the playhead as you play along — gently, and only when it helps.
  //  · View-aware: chart AND optimised grid both light up; we follow whichever
  //    the user is actually looking at (its playhead nearest the visible band's
  //    centre), so scrolling to the voicings switches following to them.
  //  · Header/footer-safe: the "band" is the clear space between the sticky
  //    topbar and the fixed transport bar, so the current chord is never parked
  //    under a header or behind the controls.
  //  · Gentle: it only scrolls when the playhead drifts to the band's edge, then
  //    places it ~30% down so several bars play before the next nudge.
  //  · Forgiving: any manual scroll suspends following for a grace window; once
  //    you settle it resumes and brings you back to the playhead.
  const followArmedRef = useRef(false);
  const followKindRef = useRef<'chart' | 'opt' | 'score' | null>(null);
  const autoScrollUntilRef = useRef(0); // ignore scroll events our own scroll makes
  const followSuspendedUntilRef = useRef(0); // grace window after a manual scroll
  const userScrollIntentUntilRef = useRef(0);
  useEffect(() => {
    const markUserScrollIntent = () => {
      userScrollIntentUntilRef.current = performance.now() + 300;
    };
    const onScrollKey = (event: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' '].includes(event.key)) {
        markUserScrollIntent();
      }
    };
    const onUserScroll = () => {
      const now = performance.now();
      const hasUserIntent = now < userScrollIntentUntilRef.current;
      if (now < autoScrollUntilRef.current && !hasUserIntent) return; // our own smooth scroll
      followSuspendedUntilRef.current = now + 3500;
    };
    window.addEventListener('wheel', markUserScrollIntent, { passive: true, capture: true });
    window.addEventListener('touchmove', markUserScrollIntent, { passive: true, capture: true });
    window.addEventListener('keydown', onScrollKey, { capture: true });
    window.addEventListener('scroll', onUserScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('wheel', markUserScrollIntent, { capture: true });
      window.removeEventListener('touchmove', markUserScrollIntent, { capture: true });
      window.removeEventListener('keydown', onScrollKey, { capture: true });
      window.removeEventListener('scroll', onUserScroll, { capture: true });
    };
  }, []);
  useEffect(() => {
    if (!playingMeasureId) {
      followArmedRef.current = false;
      followKindRef.current = null;
      return;
    }
    // The Score view's playhead is the SVG line — same follow rules apply.
    const cands = Array.from(
      document.querySelectorAll<Element>(
        '.measure-playing, .optimized-chord.playing, .notation-pane [data-playhead]',
      ),
    );
    if (cands.length === 0) return;

    // Both the app bar and the mobile view controls sit above the music.
    const header = document.querySelector('.topbar');
    const viewBar = document.querySelector('.tune-stage > .chart-footer');
    const bar = document.querySelector<HTMLElement>('.transport-bar');
    const top = useStore.getState().focusMode
      ? 0
      : Math.max(
          0,
          header?.getBoundingClientRect().bottom ?? 0,
          viewBar?.getBoundingClientRect().bottom ?? 0,
        );
    const bottom = bar ? bar.getBoundingClientRect().top : window.innerHeight;
    const bandCentre = (top + bottom) / 2;
    const overlapsBand = (el: Element) => {
      const r = el.getBoundingClientRect();
      return r.top < bottom && r.bottom > top;
    };

    // Arm only once the playhead is on screen (don't hijack a scroll if the user
    // is elsewhere when playback starts); stay armed for the rest of the run.
    if (!followArmedRef.current) {
      if (cands.some(overlapsBand)) followArmedRef.current = true;
      else return;
    }
    if (performance.now() < followSuspendedUntilRef.current) return; // user is looking elsewhere

    // Which view are you looking at? Re-pick the followed *kind* only from what's
    // currently in the band (so scrolling to the voicings switches to them), and
    // keep the current kind unless the other is clearly nearer — this stops the
    // two views fighting when both are on screen.
    const kindOf = (el: Element) =>
      el.matches('.optimized-chord') ? 'opt' : el.closest('.notation-pane') ? 'score' : 'chart';
    const dist = (el: Element) => {
      const r = el.getBoundingClientRect();
      return Math.abs((r.top + r.bottom) / 2 - bandCentre);
    };
    const inView = cands.filter(overlapsBand);
    if (inView.length) {
      const nearest = inView.reduce((a, b) => (dist(b) < dist(a) ? b : a));
      const current = inView.find((el) => kindOf(el) === followKindRef.current);
      if (!current || dist(nearest) + 120 < dist(current)) followKindRef.current = kindOf(nearest);
    }

    // Track that kind. On a bar the watched view doesn't render (e.g. a "%" repeat
    // has no optimised chord), hold position rather than jumping to the other view.
    const kindCands = cands.filter((el) => kindOf(el) === followKindRef.current);
    if (kindCands.length === 0) return;
    const target = kindCands.reduce((a, b) => (dist(b) < dist(a) ? b : a));

    // Gentle: leave it alone while it's comfortably inside the band.
    const r = target.getBoundingClientRect();
    const pad = 24;
    if (r.top >= top + pad && r.bottom <= bottom - pad) return;

    // Otherwise nudge it to ~30% down the band (room for the bars that follow).
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delta = r.top - (top + (bottom - top) * 0.3);
    const nextScrollTop = Math.max(0, window.scrollY + delta);
    if (Math.abs(nextScrollTop - window.scrollY) < 1) return;
    autoScrollUntilRef.current = performance.now() + 1200; // smooth scroll settles
    window.scrollTo({ top: nextScrollTop, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [playingMeasureId]);

  // Keep the edit form in sync when the selected chord changes UNDERNEATH it
  // (e.g. Transpose rewrites the root) — otherwise Update chord writes the
  // stale pre-transpose values back over the chord.
  useEffect(() => {
    if (!selectedChord) return;
    setForm({
      root: selectedChord.root,
      chordType: selectedChord.chordType ?? 'maj7',
      duration: selectedChord.beats,
      lead: selectedChord.targetTopNote ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedChord?.id,
    selectedChord?.root,
    selectedChord?.chordType,
    selectedChord?.beats,
    selectedChord?.targetTopNote,
  ]);

  // Optimised voicings grouped under their authored measure (first pass through
  // the form), so the Voicings view mirrors the chart's bar layout exactly.
  const optimizedByMeasure = useMemo(() => {
    const out = new Map<string, OptimizedSeqChord[]>();
    if (!sheetChords) return out;
    const byBar = new Map<number, OptimizedSeqChord[]>();
    for (const c of sheetChords) {
      const list = byBar.get(c.barIndex);
      if (list) list.push(c);
      else byBar.set(c.barIndex, [c]);
    }
    byBar.forEach((list) => list.sort((a, b) => a.beat - b.beat));
    const seen = new Set<string>();
    singleFlat.forEach((m, barIndex) => {
      if (seen.has(m.id)) return; // repeated bars share an id — show the first pass only
      const chords = byBar.get(barIndex);
      if (!chords || chords.length === 0) return;
      seen.add(m.id);
      out.set(m.id, chords);
    });
    return out;
  }, [sheetChords, singleFlat]);

  // Unrolled ("linear") view: the form expanded to full length, so the repeat's
  // second pass shows its OWN voicings (which follow on from the first pass —
  // the optimiser already voice-led through the whole flattened form) under an
  // A′ label, no voltas. The form only expands when there's something to unroll.
  // The form-cycle view reuses the same machinery with the whole form repeated.
  const hasRepeats = singleFlat.length > chart.measures.length;
  const unrolledChart = useMemo(
    () =>
      unrollChart(
        chart,
        cycleActive && cycle ? { wholeRepeats: cycle.passes, pickupOnce: true } : {},
      ),
    [chart, cycleActive, cycle],
  );
  const optimizedByUnrolledMeasure = useMemo(() => {
    const out = new Map<string, OptimizedSeqChord[]>();
    if (!sheetChords) return out;
    const byBar = new Map<number, OptimizedSeqChord[]>();
    for (const c of sheetChords) {
      const list = byBar.get(c.barIndex);
      if (list) list.push(c);
      else byBar.set(c.barIndex, [c]);
    }
    byBar.forEach((list) => list.sort((a, b) => a.beat - b.beat));
    // Unrolled measure at index i == the optimiser's barIndex i (both come from
    // the same flattened order), and ids are unique per pass, so every pass keeps
    // its own voicings — no dedup.
    unrolledChart.measures.forEach((m, barIndex) => {
      const chords = byBar.get(barIndex);
      if (chords && chords.length) out.set(m.id, chords);
    });
    return out;
  }, [sheetChords, unrolledChart]);
  // Cycling is inherently a written-out view — it forces the unrolled sheet.
  const showUnrolled = (unrollRepeats && hasRepeats) || cycleActive;
  // The band's playhead indexes its own playback form, which is the unrolled
  // order only when playback is not pre-expanded; unrolledPlayingId maps it.
  const unrolledPlayingMeasureId = unrolledPlayingId(unrolledChart.measures, {
    index: playingIndex,
    measureId: playingMeasureId,
    preExpanded: band.preExpanded,
    occurrence: band.playingOccurrence,
  });
  // The analysis lane on the chart surface: the ONE showAnalysis flag, drawn
  // as romans under the symbols of whichever chart the cell renders — the
  // reading chart (edition-transposed) on the chord score, the unrolled or
  // written chart on the diagram sheet. Off, or on a score surface (which has
  // its own lane), costs nothing.
  const chartAnalysis = useMemo(
    () => (showAnalysis && viewSurface === 'chart' ? chartAnalysisMarks(readingChart) : undefined),
    [showAnalysis, viewSurface, readingChart],
  );
  const sheetAnalysis = useMemo(
    () =>
      showAnalysis && viewSurface === 'chart'
        ? chartAnalysisMarks(showUnrolled ? unrolledChart : chart)
        : undefined,
    [showAnalysis, viewSurface, showUnrolled, unrolledChart, chart],
  );
  // The surface × lens matrix. Four cells render the classic views; the
  // chord-melody lens (both surfaces) and score+comping render through
  // ArrangementPane, whose layout follows the surface.
  // Each collapsing bar publishes its own height so the CSS can reclaim
  // exactly that much space when it slides away (see useOwnHeight).
  const measureViewBar = useOwnHeight('--collapse-height');

  const showChartCanvas = viewSurface === 'chart' && voicingLens === 'none';
  const showVoicingCanvas = viewSurface === 'chart' && voicingLens === 'comping';
  const showScoreCanvas = viewSurface === 'score' && voicingLens === 'none';
  const showMapCanvas = viewSurface === 'map' && voicingLens === 'none';
  // The map is a Practise reading: a lens (the Arrange tab, a command, a
  // recipe) turning on while the map is up lands on the chart, whose cells
  // the lenses ride. The Map button hides while a lens is on for the same
  // reason, so the surface group never names a cell that cannot draw.
  useEffect(() => {
    if (viewSurface === 'map' && voicingLens !== 'none') setViewSurface('chart');
  }, [viewSurface, voicingLens, setViewSurface]);
  // The map of the reading chart, and the chart the map is drawn from: the
  // same chart, or, for the quiz, the chart transposed into the chosen key —
  // every cell comes out the same, only the chords under the brackets move.
  const baseMap = useMemo(
    () => (showMapCanvas ? tuneMap(readingChart) : null),
    [showMapCanvas, readingChart],
  );
  const showArrangePane =
    voicingLens === 'melody' || (viewSurface === 'score' && voicingLens === 'comping');
  const arrangeLayout = viewSurface === 'score' ? ('score' as const) : ('voicings' as const);
  const arrangeLens = voicingLens === 'melody' ? ('melody' as const) : ('comping' as const);
  // The chord-melody lens needs a melody to lead with.
  const hasMelodyContent = chart.measures.some((m) => m.melody?.some((n) => !n.rest));

  useLayoutEffect(() => {
    if (!compactWorkspace || useStore.getState().focusMode) return;
    const canvas = document.querySelector('.sequence-workspace .workspace-canvas');
    if (!canvas) return;
    const headerBottom = Math.max(
      document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0,
      document.querySelector('.tune-stage > .chart-footer')?.getBoundingClientRect().bottom ?? 0,
    );
    // Switching views keeps the route but replaces the sheet. Its old scroll
    // position can put the new sheet under the taller header stack. Reveal
    // the whole sheet below all its controls before painting the new view.
    if (canvas.getBoundingClientRect().top < headerBottom) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [compactWorkspace, currentScoreId, viewSurface, voicingLens]);

  const selectSurface = (surface: ViewSurface) => {
    setMobileSheet(null);
    selectChord(null);
    setViewSurface(surface);
  };
  const selectLens = (lens: VoicingLens) => {
    setMobileSheet(null);
    selectChord(null);
    setVoicingLens(lens);
    recordFeature('lens');
  };

  useEffect(() => {
    if (!compactWorkspace) setMobileSheet(null);
  }, [compactWorkspace]);

  const chordCount = chart.measures.reduce((n, m) => n + m.chords.length, 0);

  const handleAdd = () => {
    addChord({
      root: form.root,
      chordType: form.chordType,
      beats: form.duration,
      targetTopNote: form.lead || undefined,
    });
    if (compactWorkspace) setMobileSheet(null);
  };

  const handleUpdate = () => {
    if (!selectedChord) return;
    // Send only what actually changed: an untouched Root/Chord select must not
    // re-voice the chord (which would strip iReal extras or turn N.C. real).
    updateChord(selectedChord.id, {
      ...(form.root !== selectedChord.root ? { root: form.root } : {}),
      ...(form.chordType !== (selectedChord.chordType ?? 'maj7')
        ? { chordType: form.chordType }
        : {}),
      beats: form.duration,
      targetTopNote: form.lead || undefined,
    });
    if (compactWorkspace) {
      setMobileSheet(null);
      selectChord(null);
    }
  };

  const handleRemove = () => {
    if (!selectedChord) return;
    removeChord(selectedChord.id);
    if (compactWorkspace) setMobileSheet(null);
  };

  const selectForEdit = (chord: IRealChord) => {
    selectChord(chord.id);
    setForm({
      root: chord.root,
      chordType: chord.chordType ?? 'maj7',
      duration: chord.beats,
      lead: chord.targetTopNote ?? '',
    });
  };

  const tuneRepeatOptions = Array.from({ length: 16 }, (_, index) => index + 1);
  if (band.repeatCount > 16) tuneRepeatOptions.push(band.repeatCount);

  // Tune details' own fields. Not a phone sheet form: that class's phone rule
  // stretches every field to the full width, which stacked Key, Style, Time,
  // Repeats and Transpose one per line on a phone only (user, Oct 2026: "very
  // different on mobile"). Wrapping like the desktop keeps them in two rows.
  const tuneDetailsEditor = (
    <div className="control-bar chart-meta-bar">
      <FormField label="Key">
        <div className="chart-meta-key">
          <NoteSelect
            value={keyTonic(chart.key)}
            onChange={(tonic) => transposeToKey(tonic)}
            aria-label="Chart key"
          />
          {/^.*(minor|min|m)$/i.test(chart.key ?? '') && (
            <span className="muted" style={{ fontSize: 12 }}>
              minor
            </span>
          )}
        </div>
      </FormField>
      <FormField label="Style">
        <input
          className="text-input"
          value={chart.style ?? ''}
          onChange={(e) => setChartStyle(e.target.value)}
          aria-label="Style"
          placeholder="e.g. Medium Swing"
        />
      </FormField>
      <FormField label="Time">
        <select
          value={`${chart.timeSignature[0]}/${chart.timeSignature[1]}`}
          onChange={(e) => {
            const [n, d] = e.target.value.split('/').map(Number);
            if (!setTimeSignature([n, d])) {
              window.alert(
                'That metre is too short for the existing bars. Shorten or rebar the content first.',
              );
            }
          }}
          aria-label="Time signature"
        >
          {['4/4', '3/4', '2/4', '6/8', '5/4', '12/8', '2/2'].map((signature) => (
            <option key={signature} value={signature}>
              {signature}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Repeats">
        <select
          value={chart.repeats ?? 1}
          onChange={(e) => setChartRepeats(Number(e.target.value))}
          aria-label="Repeats"
          title="How many times the whole form plays"
        >
          {tuneRepeatOptions.map((count) => (
            <option key={count} value={count}>
              ×{count}
              {count > 16 ? ' (trainer)' : ''}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Transpose">
        <div className="row">
          {TRANSPOSE_STEPS.map((step) => (
            <button
              key={step}
              type="button"
              className="btn btn-sm"
              onClick={() => transpose(step)}
              title={`Transpose ${step > 0 ? 'up' : 'down'} a semitone`}
            >
              {step > 0 ? '+½' : '−½'}
            </button>
          ))}
        </div>
      </FormField>
    </div>
  );

  const chordEditor = (
    <div className="control-bar workspace-sheet-form">
      <FormField label="Root">
        <NoteSelect
          value={form.root}
          onChange={(root) => setForm({ ...form, root })}
          aria-label="Root"
        />
      </FormField>
      <FormField label="Chord">
        <ChordTypeSelect
          value={form.chordType}
          onChange={(chordType) => setForm({ ...form, chordType })}
          aria-label="Chord type"
        />
      </FormField>
      <FormField label="Duration">
        <select
          value={form.duration}
          onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })}
          aria-label="Duration"
        >
          {[1, 2, 3, 4].map((duration) => (
            <option key={duration} value={duration}>
              {duration} beat{duration === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Lead note">
        <NoteSelect
          value={form.lead}
          onChange={(lead) => setForm({ ...form, lead })}
          includeBlank
          blankLabel="none"
          aria-label="Lead note"
        />
      </FormField>
      <div className="row workspace-sheet-actions" style={{ alignSelf: 'flex-end' }}>
        {compactWorkspace ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={selectedChord ? handleUpdate : handleAdd}
          >
            {selectedChord ? 'Update chord' : 'Add chord'}
          </button>
        ) : (
          <>
            {/* One black button: Update chord while a chord is selected,
                else Add chord. */}
            <button
              type="button"
              className={`btn btn-sm${selectedChord ? '' : ' btn-primary'}`}
              onClick={handleAdd}
            >
              Add chord
            </button>
            <button
              type="button"
              className={`btn btn-sm${selectedChord ? ' btn-primary' : ''}`}
              onClick={handleUpdate}
              disabled={!selectedChord}
            >
              Update chord
            </button>
          </>
        )}
        {selectedChord && (
          <button type="button" className="btn btn-sm btn-danger" onClick={handleRemove}>
            Remove
          </button>
        )}
      </div>
    </div>
  );

  const cycleDescription =
    cycleForm && cycle && !pianoSheet
      ? cycle.passes === 1
        ? 'Seamless loop'
        : cycle.closed
          ? `${cycle.passes} passes to return home`
          : `${cycle.passes} passes · open ending`
      : null;

  const renderSequenceViewSettings = () => {
    // Every Arrange sheet uses one scale, including chart comping. Keeping
    // the persisted score scale preserves the established score diagram size.
    const scoring = workspaceScaleKind(viewSurface, voicingLens) === 'score';
    const sizeLabel = viewSurface === 'score' ? 'Score size' : 'Chord size';

    return (
      <Popover
        label={
          <>
            <Settings size={18} strokeWidth={1.9} aria-hidden="true" />
            <span className="visually-hidden">View settings</span>
          </>
        }
        title="Sequence view settings"
        testid="sequence-view-settings"
        align="right"
      >
        <div className="settings-panel sequence-view-settings-panel">
          <div className="settings-row settings-row-stacked">
            <span className="settings-label">{sizeLabel}</span>
            {scoring ? (
              <ScaleSlider
                value={scoreScale}
                onChange={setScoreScale}
                label={sizeLabel}
                testid="score-scale"
              />
            ) : (
              <ScaleSlider
                value={chartScale}
                onChange={setChartScale}
                label="Chord size"
                testid="chart-scale"
              />
            )}
          </div>
          {voicingLens !== 'none' && (
            <>
              <div className="settings-row settings-row-stacked">
                <span className="settings-label">Chord names</span>
                <ScaleSlider
                  value={chordNameScale}
                  onChange={setChordNameScale}
                  label="Chord names"
                  testid="arrange-chord-name-scale"
                  presets={false}
                />
              </div>
              {!pianoSheet && (
                <div className="settings-row settings-row-stacked">
                  <span className="settings-label">Markers</span>
                  <div className="segmented" role="group" aria-label="Diagram markers">
                    {(
                      [
                        ['dots', 'Dots'],
                        ['notes', 'Note names'],
                        ['intervals', 'Intervals'],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={diagramMarkers === value ? 'active' : ''}
                        aria-pressed={diagramMarkers === value}
                        data-testid={`arrange-markers-${value}`}
                        onClick={() => setDiagramMarkers(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!pianoSheet && (
                <div className="settings-row settings-row-stacked">
                  <span className="settings-label">Fret numbers</span>
                  <ScaleSlider
                    value={fretNumberScale}
                    onChange={setFretNumberScale}
                    label="Fret numbers"
                    testid="arrange-fret-number-scale"
                    presets={false}
                  />
                </div>
              )}
            </>
          )}
          {/* The form controls, as ROWS like every other row here (user, Sep
              2026: the centred switch "is a different type from the other
              buttons"): label left, an On/Off button right, the cycle verdict
              as the quiet note the keep-awake row uses. Every width — the
              lens row used to carry the switch on a desktop, and its verdict
              printed under the switcher; both live here alone now (user:
              "keep it in the menu"). Comping lens only: the form controls
              shape the voicings sheet. */}
          {voicingLens === 'comping' && (
            <>
              <div className="settings-row">
                <span className="settings-label">Cycle form</span>
                <ToggleSwitch
                  on={cycleForm}
                  onChange={setCycleForm}
                  label="Cycle form"
                  testid="cycle-form"
                />
              </div>
              {cycleDescription && (
                <p className="settings-note" data-testid="cycle-note">
                  {cycleDescription}
                </p>
              )}
              {hasRepeats && !cycleForm && (
                <div className="settings-row">
                  <span className="settings-label">Unroll repeats</span>
                  <ToggleSwitch
                    on={unrollRepeats}
                    onChange={setUnrollRepeats}
                    label="Unroll repeats"
                    testid="unroll-repeats"
                  />
                </div>
              )}
            </>
          )}
          {/* Page view (Guitar Pro model): the sheet laid out as side-by-side
              pages that fit the screen. Desktop-only — the compact workspace
              has no width to spend. The chart sheets page by layout ROW, the
              score surface (and both lens overlays) by engraved SYSTEM. Every
              cell paginates, so the row is never disabled — a row that only
              exists on the right surface reads as the option not existing at
              all (user report: "i don't see the option"). */}
          {!compactWorkspace && (
            <div className="settings-row">
              <span className="settings-label">Pages</span>
              <ToggleSwitch
                on={sheetPages}
                onChange={setSheetPages}
                label="Pages"
                testid="sheet-pages-toggle"
                title="Lay the sheet out as side-by-side pages that fit the screen — widen the window for more pages"
              />
            </div>
          )}
          {/* The Studio's part vocabulary, on every score surface: one part,
              or the stacked full score. Only offered when there IS a choice —
              and on every lens, or the row seems to vanish when a lens goes
              on (user report: "the multiple part option keeps disappearing").
              ArrangementPane honours the same store field. */}
          {viewSurface === 'score' && scoreModel.parts.length > 1 && (
            <div className="settings-row">
              <span className="settings-label">Part</span>
              <select
                value={scoreViewPart}
                onChange={(e) => setScoreViewPart(Number(e.target.value))}
                aria-label="Score part"
                data-testid="score-view-part"
              >
                <option value={-1}>All parts</option>
                {scoreModel.parts.map((part, index) => (
                  <option key={part.id} value={index}>
                    {part.name || `Part ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {viewSurface === 'score' && voicingLens === 'none' && <BarsPerLineRow />}
          {viewSurface === 'score' && (
            <div className="settings-row">
              <span className="settings-label">Lyrics</span>
              <ToggleSwitch
                on={hasLyrics && showLyrics}
                onChange={setShowLyrics}
                label="Lyrics"
                testid="score-lyrics-toggle"
                disabled={!hasLyrics}
                title={hasLyrics ? 'Show or hide the sung verse lanes' : 'This tune has no lyrics'}
              />
            </div>
          )}
          <div className="settings-row">
            <span className="settings-label">Bar numbers</span>
            <ToggleSwitch
              on={showBarNumbers}
              onChange={setShowBarNumbers}
              label="Bar numbers"
              testid="score-bar-numbers-toggle"
            />
          </div>
          <div className="settings-row">
            <span className="settings-label">Highlight</span>
            <div className="swatch-row" role="group" aria-label="Playback highlight colour">
              {PLAYHEAD_TINTS.map((tint) => (
                <button
                  key={tint.id}
                  type="button"
                  className={`swatch${playheadTint === tint.id ? ' active' : ''}`}
                  style={{ background: tint.swatch }}
                  onClick={() => setPlayheadTint(tint.id)}
                  aria-pressed={playheadTint === tint.id}
                  aria-label={tint.label}
                  title={tint.label}
                  data-testid={`playhead-${tint.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </Popover>
    );
  };

  // The Chords menu: THE voicing menu (user ruling Sep 2026, superseding the
  // lens row's Family select + Options popover + engine picker, which lived
  // beside the grips and had grown into a third options surface). Top to
  // bottom: which instrument's diagrams the sheet DRAWS, which instrument the
  // comp PLAYS (the mixer's Plays picker, same band field through the same
  // one mutation, so the two can never disagree), then the voicing rows of
  // every instrument IN USE — guitar (drop family, home register, layout
  // changes, and on the comping lens the voice-leading engine and opening
  // top voice) if it is drawn OR played, piano (its family) likewise — so
  // reading grips over a piano comp still leaves the piano's voicings
  // steerable from here, and finally how chord qualities are spelled. The
  // nav drawer keeps register/layout as app-wide DEFAULTS for the lens-less
  // pages; the lens row keeps only the FORM controls (Cycle / Unroll). The
  // trigger wears the drawn instrument's icon, so the state reads without
  // opening anything (the key chip's "A♭ 8vb" rule).
  const renderChordMenu = () => (
    <Popover
      label={
        <>
          {diagramInstrument === 'piano' ? (
            <Piano size={18} strokeWidth={1.9} aria-hidden="true" />
          ) : (
            <Guitar size={18} strokeWidth={1.9} aria-hidden="true" />
          )}
          <span className="visually-hidden">Chords</span>
        </>
      }
      title="Chords — diagrams and notation"
      testid="chords-menu"
      align="right"
    >
      <div className="settings-panel chords-menu-panel">
        <div className="settings-row">
          <span className="settings-label">Diagrams</span>
          <div className="segmented" role="group" aria-label="Chord diagrams">
            {CHORD_DIAGRAM_OPTIONS.map((instrument) => (
              <button
                key={instrument.id}
                type="button"
                className={diagramInstrument === instrument.id ? 'active' : ''}
                onClick={() => setDiagramInstrument(instrument.id)}
                aria-pressed={diagramInstrument === instrument.id}
                title={instrument.hint}
                data-testid={`chord-diagrams-${instrument.id}`}
              >
                {instrument.label}
              </button>
            ))}
          </div>
        </div>
        {/* The BACKING instrument: the mixer's Plays picker, bound to the same
            band field through the same one mutation (patchBand, #123), so the
            two can never disagree. It sits beside Diagrams because the voicing
            rows below follow BOTH roles. */}
        <BandScopeRow
          editor={bandEditor}
          playingLabel={band.playingBandLabel}
          testid="chords-scope"
        />
        <div className="settings-row">
          <span className="settings-label">Backing</span>
          <div className="segmented" role="group" aria-label="Backing instrument">
            {BACKING_OPTIONS.map((instrument) => (
              <button
                key={instrument.id}
                type="button"
                className={backingIs(instrument.id) ? 'active' : ''}
                onClick={() => patchBand({ compInstrument: instrument.id })}
                aria-pressed={backingIs(instrument.id)}
                title={`The comp plays ${instrument.label.toLowerCase()}`}
                data-testid={`chords-backing-${instrument.id}`}
              >
                {instrument.label}
              </button>
            ))}
          </div>
        </div>
        {/* Chord melody density (Sep 2026): a grip per harmony change, or one
            under every melody note — the block-chord texture. It shapes the
            SEQUENCE both instruments voice, so it sits above the per-instrument
            rows and shows on the melody lens only. */}
        {voicingLens === 'melody' && (
          <div className="settings-row">
            <span className="settings-label">Chord melody</span>
            <div className="segmented" role="group" aria-label="Chord melody density">
              <button
                type="button"
                className={chordMelodyDensity === 'changes' ? 'active' : ''}
                onClick={() => setChordMelodyDensity('changes')}
                aria-pressed={chordMelodyDensity === 'changes'}
                data-testid="chord-melody-changes"
              >
                Changes
              </button>
              <button
                type="button"
                className={chordMelodyDensity === 'every-note' ? 'active' : ''}
                onClick={() => setChordMelodyDensity('every-note')}
                aria-pressed={chordMelodyDensity === 'every-note'}
                data-testid="chord-melody-every-note"
              >
                Every note
              </button>
            </div>
          </div>
        )}
        {/* Guitar voicings: the grip search's knobs, shown while guitar is
            DRAWN or PLAYED. Family and register/layout bind the same store
            fields the nav drawer offers as app-wide defaults (the
            compPianoFamily precedent); the engine and the opening top voice
            steer the comping walk, so they show on the comping lens only. */}
        {(diagramInstrument === 'guitar' || compInstrument === 'guitar') && (
          <section className="settings-section" aria-labelledby="chords-guitar-heading">
            <h3 id="chords-guitar-heading" className="settings-section-title">
              Guitar voicings
            </h3>
            <div className="settings-row">
              <label className="settings-label" htmlFor="chords-guitar-family">
                Family
              </label>
              <select
                id="chords-guitar-family"
                value={voicingFamily}
                onChange={(event) => setVoicingFamily(event.target.value as VoicingFamilySelection)}
                aria-label="Voicing family"
                data-testid="voicing-family"
              >
                {AVAILABLE_VOICING_FAMILY_SELECTION_IDS.map((selection) => (
                  <option
                    key={selection}
                    value={selection}
                    title={voicingFamilySelectionDescription(selection)}
                  >
                    {voicingFamilySelectionLabel(selection)}
                  </option>
                ))}
              </select>
            </div>
            {voicingFamily !== 'joe-pass' && (
              <>
                <div className="settings-row">
                  <span className="settings-label">Home register</span>
                  <div className="segmented" role="group" aria-label="Home register">
                    <button
                      type="button"
                      className={stringSet === 'middle' ? 'active' : ''}
                      onClick={() => setStringSet('middle')}
                      aria-pressed={stringSet === 'middle'}
                      data-testid="lens-stringset-middle"
                    >
                      Lower
                    </button>
                    <button
                      type="button"
                      className={stringSet === 'upper' ? 'active' : ''}
                      onClick={() => setStringSet('upper')}
                      aria-pressed={stringSet === 'upper'}
                      data-testid="lens-stringset-upper"
                    >
                      Upper
                    </button>
                  </div>
                </div>
                <label
                  className="switch"
                  title="Voice-lead across both layouts — the home layout starts the line, and later chords may change layout when it means less movement"
                >
                  <input
                    type="checkbox"
                    checked={freeStringSet}
                    onChange={(event) => setFreeStringSet(event.target.checked)}
                    data-testid="lens-free-stringset"
                  />
                  Allow layout changes
                </label>
              </>
            )}
            {voicingLens === 'comping' && (
              <>
                <div className="settings-row" title={activeEngine.hint}>
                  <label className="settings-label" htmlFor="chords-voice-leading">
                    Voice leading
                  </label>
                  <select
                    id="chords-voice-leading"
                    value={activeEngine.id}
                    onChange={(e) => setVoiceLeadingEngine(e.target.value as VoiceLeadingEngineId)}
                    aria-label="Voice-leading engine"
                    data-engine-id={activeEngine.id}
                    data-engine-version={activeEngine.version}
                    data-testid="voice-leading-engine"
                  >
                    {VOICE_LEADING_ENGINES.map((engine) => (
                      <option key={engine.id} value={engine.id} title={engine.hint}>
                        {engine.label}
                      </option>
                    ))}
                  </select>
                </div>
                {voicingFamily !== 'joe-pass' && (
                  <div className="settings-row voicing-options-opening">
                    <label className="settings-label" htmlFor="chords-opening-voice">
                      Opening top voice
                    </label>
                    <select
                      id="chords-opening-voice"
                      value={startingInversion}
                      onChange={(e) => setStartingInversion(Number(e.target.value))}
                      aria-label="Opening top voice"
                      data-testid="starting-inversion"
                      disabled={openingTopVoices.length === 0}
                    >
                      {openingTopVoices.length > 0 ? (
                        openingTopVoices.map(({ rotation, note, tone }) => (
                          <option key={rotation} value={rotation}>
                            {note} ({tone})
                          </option>
                        ))
                      ) : (
                        <option value={startingInversion}>Add a chord first</option>
                      )}
                    </select>
                  </div>
                )}
              </>
            )}
          </section>
        )}
        {/* Piano voicings, shown while piano is DRAWN or PLAYED — so a guitar
            comp under piano diagrams, or piano backing under guitar grips,
            leaves the family steerable here as well as in the mixer. Same
            band field through the same one mutation, so the two selects can
            never disagree: the sheet walk and the playback walk both read it. */}
        {(diagramInstrument === 'piano' || compInstrument !== 'guitar') && (
          <section className="settings-section" aria-labelledby="chords-piano-heading">
            <h3 id="chords-piano-heading" className="settings-section-title">
              {compInstrument === 'piano-left' ? 'Left-hand voicings' : 'Piano voicings'}
            </h3>
            <div className="settings-row">
              <label className="settings-label" htmlFor="chords-piano-family">
                Family
              </label>
              <select
                id="chords-piano-family"
                value={effectivePianoFamily(compInstrument, compPianoFamily)}
                onChange={(e) => patchBand({ compPianoFamily: e.target.value as PianoCompFamily })}
                title={
                  (compInstrument === 'piano-left'
                    ? PIANO_LEFT_HAND_FAMILIES
                    : PIANO_COMP_FAMILIES
                  ).find((f) => f.id === effectivePianoFamily(compInstrument, compPianoFamily))
                    ?.hint
                }
                aria-label="Piano voicing family"
                data-testid="chords-piano-family"
              >
                {(compInstrument === 'piano-left'
                  ? PIANO_LEFT_HAND_FAMILIES
                  : PIANO_COMP_FAMILIES
                ).map((f) => (
                  <option key={f.id} value={f.id} title={f.hint}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </section>
        )}
        {/* Your pins on the lane being drawn (voicingPins.ts): a count and one
            Reset, present on every lens cell and disabled at zero (the
            Pages-row lesson: a row that only appears once you have pins reads
            as the feature not existing). */}
        {voicingLens !== 'none' && (
          <div className="settings-row">
            <span className="settings-label">Pinned chords</span>
            <span className="settings-note" data-testid="pins-count">
              {pinnedOnLane}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              data-testid="pins-reset"
              disabled={pinnedOnLane === 0}
              onClick={() => clearVoicingPins(currentPinLane)}
            >
              Reset
            </button>
          </div>
        )}
        <div className="settings-row">
          <span className="settings-label">Notation</span>
          <div className="segmented" role="group" aria-label="Chord notation">
            {CHORD_SYMBOL_STYLES.map((style) => (
              <button
                key={style.id}
                type="button"
                className={chordSymbolStyle === style.id ? 'active' : ''}
                onClick={() => setChordSymbolStyle(style.id)}
                aria-pressed={chordSymbolStyle === style.id}
                title={style.sample}
                data-testid={`chord-style-${style.id}`}
              >
                {style.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Popover>
  );

  // Global view actions have one stable home beside the workspace tabs. Modes
  // may change the popover's contents, but never the trigger's placement.
  const renderWorkspaceViewActions = () => (
    <div className="workspace-view-actions">
      {showChartCanvas && (
        <button
          type="button"
          className="btn btn-sm workspace-edit-toggle"
          aria-label="Edit chords"
          title="Edit chords in the chart"
          aria-pressed={editMode}
          data-testid="workspace-edit-toggle"
          onClick={() => {
            setEditMode((value) => !value);
            setMobileSheet(null);
          }}
        >
          <Pencil size={15} strokeWidth={1.9} aria-hidden="true" />
          <span>Edit chords</span>
        </button>
      )}
      {showChartCanvas && editMode && (
        <button
          type="button"
          className="btn btn-sm btn-primary workspace-add-chord"
          aria-label="Add chord"
          title="Add chord"
          onClick={() => {
            selectChord(null);
            if (compactWorkspace) setMobileSheet('chord');
          }}
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          <span>Add chord</span>
        </button>
      )}
      {renderChordMenu()}
      {renderSequenceViewSettings()}
      <FocusToggle />
    </div>
  );

  const renderExportActions = () => (
    <>
      {iRealExport.warnings.length > 0 && (
        <p
          className="muted"
          role="note"
          data-testid="ireal-export-warning"
          style={{ maxWidth: 280, margin: '0.35rem 0.75rem 0.65rem', fontSize: 12 }}
        >
          iReal Pro will keep {iRealExport.warnings.length} written Feel cue
          {iRealExport.warnings.length === 1 ? '' : 's'} as visible staff text, but cannot preserve
          their typed playback behaviour.
        </p>
      )}
      {/* The door to paper — and on a phone, to "Save as PDF", which lives
          inside the system print sheet. Share → Print is three taps into a
          menu nobody associates with a web app; this row says the sheet
          prints. The same command the ⌘K palette runs. */}
      <button
        className="menu-item"
        data-testid="export-print"
        onClick={() => {
          void executeCommand('print_sheet', {});
        }}
      >
        Print
      </button>
      <button
        className="menu-item"
        data-testid="export-copy-link"
        onClick={() => {
          const url = readingLinkUrl(window.location.href, scoreEdition);
          const copy = navigator.clipboard?.writeText(url);
          if (!copy) {
            window.prompt('Link (copy):', url);
            return;
          }
          copy.then(
            () => {
              setCopiedLink(true);
              window.setTimeout(() => setCopiedLink(false), 2000);
            },
            () => window.prompt('Link (copy):', url),
          );
        }}
      >
        {copiedLink ? 'Link copied' : 'Copy link'}
      </button>
      <button
        className="menu-item"
        data-testid="export-link"
        onClick={() => {
          const url = iRealExport.url;
          navigator.clipboard?.writeText(url).catch(() => {});
          window.prompt('iReal Pro link (copy):', url);
        }}
      >
        Copy iReal Pro link
      </button>
      <button
        className="menu-item"
        onClick={() => download(`${slug(chart.title)}.html`, toIRealHTML(chart), 'text/html')}
      >
        Download .html
      </button>
      <button
        className="menu-item"
        onClick={() =>
          download(`${slug(chart.title)}.json`, JSON.stringify(chart, null, 2), 'application/json')
        }
      >
        Download .json
      </button>
      <button
        className="menu-item menu-item-danger"
        onClick={() => {
          if (window.confirm('Clear the entire chart? This cannot be undone.')) clearChart();
        }}
      >
        Clear all…
      </button>
    </>
  );

  const renderNewTuneForm = (close: () => void) => (
    <NewTuneForm
      onCreate={(meta) => {
        newTune(meta);
        clearStudyContext();
        close();
      }}
    />
  );

  const renderTuneFileActions = (close: () => void) => (
    <>
      <details className="tune-file-section">
        <summary data-testid="new-tune">New tune</summary>
        {renderNewTuneForm(close)}
      </details>
      <button
        type="button"
        className="menu-item"
        data-testid="tune-import"
        onClick={() => {
          setShowImport(true);
          close();
        }}
      >
        Import…
      </button>
      <details className="tune-file-section">
        <summary data-testid="export-menu">Export</summary>
        {renderExportActions()}
      </details>
    </>
  );

  return (
    <div
      className={`sequence-workspace${band.showTransportBar ? ' has-transport-bar' : ''}`}
      data-workspace-surface={viewSurface}
      data-workspace-lens={voicingLens}
      data-edit-mode={editMode || undefined}
      data-chart-font={VISUAL_STYLE}
      data-realbook=""
      data-chart-scale=""
      data-score-scale=""
      style={scaleVars({ chart: chartScale, score: scoreScale, chordName: chordNameScale })}
      data-playhead={playheadTint}
      data-voicing-family={voicingFamily}
    >
      <h1 className="visually-hidden">Tune Workbench</h1>
      <FocusControls onPlay={() => band.playAll()} />

      <TuneWorkspaceHeader
        mode={voicingLens === 'none' ? 'practise' : 'arrange'}
        details={tuneDetailsEditor}
        fileActions={renderTuneFileActions}
        playbackStatus={bandPlaybackStatus(band)}
        formCycling={cycleActive}
        onModeChange={() => {
          setEditMode(false);
          setMobileSheet(null);
          selectChord(null);
        }}
      />

      {headerSlot &&
        createPortal(
          <div className="header-tune-actions" aria-label="Tune actions">
            <PlaylistsMenu />
            {!compactWorkspace && (
              <Popover
                label={
                  <span className="tune-action-label">
                    <FilePlus2 size={16} strokeWidth={1.8} aria-hidden="true" />
                    <span>New</span>
                  </span>
                }
                title="Start a new tune with a blank canvas"
                testid="new-tune"
                align="right"
              >
                {renderNewTuneForm}
              </Popover>
            )}
            {!compactWorkspace && (
              <Popover
                label={
                  <span className="tune-action-label">
                    <Download size={16} strokeWidth={1.8} aria-hidden="true" />
                    <span>Export</span>
                  </span>
                }
                title="Share, download or clear this chart"
                testid="export-menu"
                align="right"
              >
                {renderExportActions()}
              </Popover>
            )}
          </div>,
          headerSlot,
        )}

      {studyContext && (
        <aside className="workbench-study-context" data-testid="workbench-study-context">
          <div>
            <span className="label">Guided study · Chapter {studyContext.chapter.number}</span>
            <strong>{studyContext.study.label}</strong>
            <p>{studyContext.study.note}</p>
          </div>
          <div className="workbench-study-actions">
            <Link
              className="btn btn-sm btn-primary"
              to={`${APP_ROUTES.studies}/${studyContext.chapter.id}`}
            >
              Return to lesson
            </Link>
            <button type="button" className="btn btn-sm btn-ghost" onClick={clearStudyContext}>
              Exit study
            </button>
          </div>
        </aside>
      )}

      {showImport && (
        <ImportPanel
          onClose={() => setShowImport(false)}
          onImported={(title) => {
            setShowImport(false);
            clearStudyContext();
            void title;
          }}
        />
      )}

      {/* One responsive workspace switcher. CSS changes its layout; React does
          not maintain or render a second mobile view state. */}
      <div className="tune-stage" data-testid="tune-stage">
        <div className="chart-footer" ref={measureViewBar}>
          {/* The sheet keeps its musical heading; shared document actions and
              workspace destinations live above these chart view controls. */}
          {/* Practise is the plain chart/score. Arrangement choices only
              appear while arranging; the workspace tabs turn the overlay off. */}
          <div className="workspace-view-groups">
            <div
              className="view-toggle workspace-view-toggle"
              role="group"
              aria-label="Notation view"
            >
              {VIEW_SURFACES.filter(
                (surface) => surface.id !== 'map' || voicingLens === 'none',
              ).map((surface) => (
                <button
                  key={surface.id}
                  type="button"
                  className={`view-toggle-btn${viewSurface === surface.id ? ' active' : ''}`}
                  aria-pressed={viewSurface === surface.id}
                  title={surface.description}
                  data-testid={`${compactWorkspace ? 'mobile-' : ''}view-${surface.id}`}
                  onClick={() => selectSurface(surface.id)}
                >
                  {surface.label}
                </button>
              ))}
            </div>
            {voicingLens !== 'none' && (
              <div
                className="view-toggle workspace-view-toggle workspace-lens-toggle"
                role="group"
                aria-label="Voicings"
              >
                {VOICING_LENSES.filter((lens) => lens.id !== 'none').map((lens) => {
                  const needsMelody = lens.id === 'melody' && !hasMelodyContent;
                  return (
                    <button
                      key={lens.id}
                      type="button"
                      className={`view-toggle-btn${voicingLens === lens.id ? ' active' : ''}`}
                      aria-pressed={voicingLens === lens.id}
                      // Pin the accessible name to the full label: the visible text
                      // shrinks on phones ("Chord melody" → "Melody") but role-based
                      // queries and screen readers see one stable name everywhere.
                      aria-label={lens.label}
                      disabled={needsMelody}
                      title={
                        needsMelody
                          ? 'Add or import a melody in Score Studio to build a chord-melody arrangement'
                          : lens.description
                      }
                      data-testid={`${compactWorkspace ? 'mobile-' : ''}lens-${lens.id}`}
                      onClick={() => selectLens(lens.id)}
                    >
                      <span className="lens-label-full">{lens.label}</span>
                      <span className="lens-label-short">{lens.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {renderWorkspaceViewActions()}
        </div>

        {/* The chart */}
        {showChartCanvas ? (
          <div className={`workspace-layout${editMode ? ' is-editing' : ''}`}>
            <section className="workspace-canvas chart-workspace-canvas">
              {chordCount === 0 && chart.measures.length <= 1 ? (
                <div className="empty-hint" data-testid="empty-canvas">
                  {/* A blank sheet still needs its tune identity before
                      there is any music to carry a printed masthead. */}
                  <p className="empty-hint-identity" data-testid="empty-canvas-identity">
                    <strong>{chart.title.trim() || 'Untitled tune'}</strong>
                    {chart.composer ? ` · ${chart.composer}` : ''}
                    {chart.key ? ` · Key ${chart.key}` : ''}
                  </p>
                  <p>Open a tune from Home, or choose New to start a chart from scratch.</p>
                </div>
              ) : (
                <>
                  {/* Reading vs editing is the whole switch: outside edit mode
                      the sheet is paper — no hover invitations, no selection
                      rings, and a tap on a bar (chords included) is the start
                      cursor instead of selecting things it won't edit. */}
                  <PagedSheet
                    reading
                    chart={readingChart}
                    active={pagesActive}
                    header={<ChartHeader chart={chart} edition={readingEditionLabel} />}
                    render={(rowWindow, cells, _slots, readingLayout) => (
                      <ChartView
                        readingLayout={readingLayout}
                        chart={readingChart}
                        cellsPerRow={cells}
                        rowWindow={rowWindow}
                        selectedChordId={editMode ? selectedChordId : null}
                        selectedMeasureId={editMode ? selectedMeasureId : null}
                        passageMeasureIds={passageIdSet}
                        reharmMeasureIds={reharmIdSet}
                        analysisMarks={chartAnalysis}
                        insertionMeasureId={editMode ? insertionMeasureId : null}
                        playingMeasureId={playingMeasureId}
                        onSelectChord={
                          editMode
                            ? (id) => {
                                const found = chart.measures
                                  .flatMap((m) => m.chords)
                                  .find((c) => c.id === id);
                                if (found) {
                                  selectForEdit(found);
                                  if (compactWorkspace) setMobileSheet('chord');
                                }
                              }
                            : undefined
                        }
                        onSelectMeasure={
                          editMode
                            ? (id) => {
                                selectMeasure(id);
                                if (compactWorkspace) setMobileSheet('measure');
                              }
                            : undefined
                        }
                        onSelectPassage={onSelectPassage}
                        onTapMeasure={editMode ? undefined : band.seekToBar}
                        cursorMeasureId={band.startCursor?.measureId}
                      />
                    )}
                  />
                </>
              )}
            </section>
            {!compactWorkspace && editMode && (
              <aside className="workspace-inspector" aria-label="Chart editor">
                <div className="workspace-inspector-head">
                  <div>
                    <span className="label">Chart editor</span>
                    <strong>{selectedChord?.symbol ?? 'Add a chord'}</strong>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={() => setEditMode(false)}>
                    Done
                  </button>
                </div>
                {chordEditor}
                {selectedMeasureId && !selectedChord && (
                  <MeasureEditor measureId={selectedMeasureId} />
                )}
                {selectedChord && selectedChord.chordType && (
                  <VoicingAnalysis
                    chord={selectedChord}
                    stringSet={stringSet}
                    voicingFamily={voicingFamily}
                    avoidB9={avoidB9}
                  />
                )}
              </aside>
            )}
          </div>
        ) : chordCount === 0 && chart.measures.length <= 1 ? (
          <div className="empty-hint" data-testid="empty-canvas">
            <p className="empty-hint-identity" data-testid="empty-canvas-identity">
              <strong>{chart.title.trim() || 'Untitled tune'}</strong>
              {chart.composer ? ` · ${chart.composer}` : ''}
              {chart.key ? ` · Key ${chart.key}` : ''}
            </p>
            <p>Open a tune from Home, or choose New to start a chart from scratch.</p>
          </div>
        ) : null}

        {/* The tune map (Oct 2026): the chord lane as cadence cells relative
            to the home key, on the chart's own grid so the playhead, the loop
            and the transport carry over. The controls sit at the top of the
            canvas: the summary line, the check row's detail, and the key the
            map is drawn in (the quiz: a key other than the tune's hides the
            chords until a bar is tapped). */}
        {showMapCanvas && baseMap && !(chordCount === 0 && chart.measures.length <= 1) && (
          <section className="workspace-canvas chart-workspace-canvas map-workspace-canvas">
            <div className="map-controls" data-testid="map-controls">
              {tuneMapLayout === 'card' && (
                <div className="segmented map-layout-control" role="group" aria-label="Map layout">
                  {MAP_LAYOUTS.map((layout) => (
                    <button
                      key={layout.id}
                      type="button"
                      className={tuneMapLayout === layout.id ? 'active' : ''}
                      aria-pressed={tuneMapLayout === layout.id}
                      title={layout.description}
                      data-testid={`map-layout-${layout.id}`}
                      onClick={() => setTuneMapLayout(layout.id)}
                    >
                      {layout.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="map-control-group">
                {tuneMapLayout === 'grid' && (
                  <div
                    className="segmented map-layout-control"
                    role="group"
                    aria-label="Map layout"
                  >
                    {MAP_LAYOUTS.map((layout) => (
                      <button
                        key={layout.id}
                        type="button"
                        className={tuneMapLayout === layout.id ? 'active' : ''}
                        aria-pressed={tuneMapLayout === layout.id}
                        title={layout.description}
                        data-testid={`map-layout-${layout.id}`}
                        onClick={() => setTuneMapLayout(layout.id)}
                      >
                        {layout.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="map-key-control" role="group" aria-label="Chords">
                  <span>Chords</span>
                  <div className="segmented">
                    {MAP_DETAILS.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        className={tuneMapDetail === level.id ? 'active' : ''}
                        aria-pressed={tuneMapDetail === level.id}
                        title={level.description}
                        data-testid={`map-detail-${level.id}`}
                        onClick={() => setTuneMapDetail(level.id)}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  data-testid="map-print"
                  onClick={() => void executeCommand('print_sheet', {})}
                >
                  Print
                </button>
              </div>
            </div>
            {tuneMapLayout === 'card' ? (
              <>
                <ChartHeader chart={chart} edition={readingEditionLabel} />
                <TuneMapCard
                  chart={readingChart}
                  map={baseMap}
                  detail={tuneMapDetail}
                  playingMeasureId={playingMeasureId}
                />
                {printing && (
                  <PrintSheet
                    className="map-card-print"
                    count={1}
                    render={() => (
                      <>
                        <ChartHeader chart={chart} edition={readingEditionLabel} />
                        <TuneMapCard chart={readingChart} map={baseMap} detail={tuneMapDetail} />
                      </>
                    )}
                  />
                )}
              </>
            ) : (
              <PagedSheet
                chart={readingChart}
                active={pagesActive}
                header={<ChartHeader chart={chart} edition={readingEditionLabel} />}
                render={(rowWindow, cells) => (
                  <TuneMapView
                    chart={readingChart}
                    map={baseMap}
                    detail={tuneMapDetail}
                    cellsPerRow={cells}
                    rowWindow={rowWindow}
                    passageMeasureIds={passageIdSet}
                    playingMeasureId={playingMeasureId}
                    onSelectPassage={onSelectPassage}
                    onTapMeasure={band.seekToBar}
                    cursorMeasureId={band.startCursor?.measureId}
                  />
                )}
              />
            )}
          </section>
        )}

        {/* The tune as notation (unification Phase 3): the same score model the
          Score studio edits, engraved in place with the band playhead. */}
        {showScoreCanvas && !(chordCount === 0 && chart.measures.length <= 1) && (
          <section className="workspace-canvas score-workspace-canvas">
            {/* No tempo chip: the transport owns tempo in this view. The
                masthead rides the pane's header slot so page view can size it
                to the spread (same rule as the chart's PagedSheet). */}
            <NotationPane
              band={band}
              pages={pagesActive}
              header={<ChartHeader chart={chart} edition={editionSheetLabel(scoreEdition)} />}
            />
          </section>
        )}

        {showArrangePane && !(chordCount === 0 && chart.measures.length <= 1) && (
          <section className="workspace-canvas arrange-workspace-canvas">
            <ArrangementPane
              band={band}
              layout={arrangeLayout}
              lens={arrangeLens}
              alternativeShapes={alternativeShapes}
              preferredAlternativeIds={preferredAlternativeIds}
              pages={pagesActive}
              header={<ChartHeader chart={chart} />}
            />
          </section>
        )}

        {/* Optimised output */}
        {/* Same plain canvas as every other cell — the old desktop
          analysis-panel card gave this one view a boxed white background. */}
        {optimized && showVoicingCanvas && (
          <section className="workspace-canvas voicing-workspace-canvas">
            {showVoicingCanvas && (
              <>
                <PagedSheet
                  chart={showUnrolled ? unrolledChart : chart}
                  scale={scoreScale}
                  active={pagesActive}
                  header={<ChartHeader chart={chart} />}
                  render={(rowWindow, cells, slots) => (
                    <GuitarChartView
                      chart={showUnrolled ? unrolledChart : chart}
                      scale={scoreScale}
                      cellsPerRow={cells}
                      slotChords={slots}
                      rowWindow={rowWindow}
                      analysisMarks={sheetAnalysis}
                      byMeasure={showUnrolled ? optimizedByUnrolledMeasure : optimizedByMeasure}
                      playingMeasureId={showUnrolled ? unrolledPlayingMeasureId : playingMeasureId}
                      passageMeasureIds={showUnrolled ? undefined : passageIdSet}
                      onSelectPassage={showUnrolled ? undefined : onSelectPassage}
                      onTapMeasure={band.seekToBar}
                      cursorMeasureId={band.startCursor?.measureId}
                    />
                  )}
                />
              </>
            )}
          </section>
        )}

        {compactWorkspace && mobileSheet === 'chord' && (
          <WorkspaceSheet
            title={selectedChord ? selectedChord.symbol : 'Add chord'}
            onClose={() => {
              setMobileSheet(null);
              selectChord(null);
            }}
          >
            {chordEditor}
            {selectedChord && selectedChord.chordType && (
              <VoicingAnalysis
                chord={selectedChord}
                stringSet={stringSet}
                voicingFamily={voicingFamily}
                avoidB9={avoidB9}
              />
            )}
          </WorkspaceSheet>
        )}

        {compactWorkspace && mobileSheet === 'measure' && selectedMeasureId && (
          <WorkspaceSheet title="Bar settings" onClose={() => setMobileSheet(null)}>
            <MeasureEditor measureId={selectedMeasureId} />
          </WorkspaceSheet>
        )}
      </div>

      {/* About rides this bar's drawer (the book icon), so reading where a tune
          sits in the repertoire never scrolls the score away. */}
      <BandTransport band={band} />
    </div>
  );
}

function keyTonic(key: string | undefined): string {
  if (!key) return 'C';
  const m = /^([A-G][#b]?)/.exec(key.trim());
  return m ? m[1] : 'C';
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="control-group">
      <span className="label">{label}</span>
      {children}
    </div>
  );
}

/** The "New tune" form: title, composer, style and key → a blank canvas.
 * Local draft state; remounts (and so resets) each time the popover opens. */
function NewTuneForm({ onCreate }: { onCreate: (meta: NewTuneInput) => void }) {
  const [title, setTitle] = useState('');
  const [composer, setComposer] = useState('');
  const [style, setStyle] = useState('');
  const [key, setKey] = useState('C');
  return (
    <form
      className="new-tune-form"
      onSubmit={(e) => {
        e.preventDefault();
        onCreate({ title, composer, style, key });
      }}
    >
      <FormField label="Title">
        <input
          className="text-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Untitled"
          aria-label="New tune title"
          data-testid="new-tune-title"
          autoFocus
        />
      </FormField>
      <FormField label="Composer">
        <input
          className="text-input"
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Optional"
          aria-label="New tune composer"
        />
      </FormField>
      <FormField label="Style">
        <input
          className="text-input"
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          placeholder="e.g. Medium Swing"
          aria-label="New tune style"
        />
      </FormField>
      <FormField label="Key">
        <NoteSelect value={key} onChange={setKey} aria-label="New tune key" />
      </FormField>
      <button type="submit" className="btn btn-sm btn-primary" data-testid="new-tune-create">
        Create
      </button>
    </form>
  );
}

function VoicingAnalysis({
  chord,
  stringSet,
  voicingFamily,
  avoidB9,
}: {
  chord: IRealChord;
  stringSet: 'middle' | 'upper';
  voicingFamily: VoicingFamilySelection;
  avoidB9: boolean;
}) {
  const setPreferredInversion = useStore((s) => s.setPreferredInversion);
  const { inspect } = useInspector();
  const chordType = chord.chordType!;

  // A combined selection analyses BOTH families' grips; each row still
  // belongs to one physical family (the label carries it when mixed).
  const families = familiesOf(voicingFamily);
  const analyses = families.flatMap((family) =>
    ([0, 1, 2, 3] as Inversion[]).map((inv) => {
      const voicing = generateChordVoicing(chord.root, chordType, inv, stringSet, family);
      return {
        family,
        inv,
        voicing,
        usable: voicing ? shouldUseVoicing(voicing, stringSet, chordType, avoidB9) : false,
        avoid: voicing ? hasFlatNineAvoidInterval(voicing, stringSet) : false,
      };
    }),
  );
  const available = analyses.filter((a) => a.voicing && a.usable);
  const avoided = analyses.filter((a) => a.voicing && !a.usable);
  const requested = analyses.find((a) => a.inv === chord.preferredInversion);
  const requestedLabel = requested?.voicing
    ? voicingIdentityLabel(requested.voicing, chord.root, chordType)
    : Number.isInteger(chord.preferredInversion)
      ? `${topVoiceToneName(chordType, chord.preferredInversion!)} on top`
      : null;

  return (
    <section className="analysis-panel voicing-analysis">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>
          {chord.symbol} · {voicingFamilySelectionLabel(voicingFamily)} analysis
        </h3>
        {Number.isInteger(chord.preferredInversion) && (
          <button className="btn btn-sm" onClick={() => setPreferredInversion(chord.id, null)}>
            Clear lock
          </button>
        )}
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        {available.length} available, {avoided.length} avoided
        {requestedLabel ? ` · ${requestedLabel} requested` : ''}
      </p>

      <h4 style={{ marginBottom: 6 }}>Available</h4>
      <div className="voicing-options">
        {available.map((a) => (
          <button
            key={`${a.family}:${a.inv}`}
            className={`voicing-option${chord.preferredInversion === a.inv ? ' selected' : ''}`}
            onClick={() => setPreferredInversion(chord.id, a.inv)}
            onDoubleClick={() =>
              inspect({
                fingering: a.voicing!,
                rootDisplay: chord.root,
                chordType,
                symbol: chord.symbol,
                inversion: a.inv,
                stringSet,
              })
            }
          >
            {voicingIdentityLabel(a.voicing!, chord.root, chordType)}
            <br />
            <span className="muted">
              {topNoteOf(a.voicing!, stringSet)} on top
              {families.length > 1 ? ` · ${voicingFamilyLabel(a.family)}` : ''}
            </span>
          </button>
        ))}
      </div>

      {avoided.length > 0 && (
        <>
          <h4 style={{ margin: '12px 0 6px' }}>Avoided (b9 avoid interval)</h4>
          <div className="voicing-options">
            {avoided.map((a) => (
              <div key={`${a.family}:${a.inv}`} className="voicing-option avoided">
                {voicingIdentityLabel(a.voicing!, chord.root, chordType)}
                <br />
                <span className="muted">b9 clash</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
